import { MainPanel } from '@calendly/booking-kit'
import { Loader } from '@calendly/ui/components/loader'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { useFlag } from '@calendly/feature-check-react'

import LayoutContainer from 'components/layout/container'
import ErrorAlert from 'components/error/alert'
import { ConfirmationPageDataWrapper, useConfirmationPageData } from 'components/confirmation/useConfirmationPageData'
import { useStatsigLayer } from 'hooks/use-statsig-layer'
import { BookingStorage } from 'components/confirmation/booking-storage'
import { ConfirmationRedesignFooterSection } from 'components/confirmation/layout'
import { PhoneVerificationModal } from 'components/phone_verification'
import { pendingPhoneVerificationSelector, unverifiedPhoneNumberSelector, uuidSelector } from 'selectors/invitee'
import { useVerifySmsOtpMutation, useResendSmsOtpMutation } from 'root-api'
import { useI18n } from 'locales'

import { MeetingPollConfirmation } from './meeting_poll_confirmation'
import { Confirmation as NewConfirmation } from './new_confirmation'
import { PersonalizedConfirmation } from './personalized_confirmation'
import { ConfirmationPage as OldConfirmation } from './legacy'

const SMS_OTP_ERROR_KEYS = {
  verification_incorrect: 'booking.phone_verification.error.verification_incorrect',
  verification_expired: 'booking.phone_verification.error.verification_expired',
  verification_rate_limited: 'booking.phone_verification.error.verification_rate_limited',
}

const ConfirmationMainPanel = ({ children }) => {
  const bookingAppRedesign = useFlag('booking_app_redesign', false)

  return (
    <LayoutContainer expanded hideBranding={bookingAppRedesign} fitContent={bookingAppRedesign}>
      <MainPanel>
        {children}
        {bookingAppRedesign && <ConfirmationRedesignFooterSection />}
      </MainPanel>
    </LayoutContainer>
  )
}

const CurrentConfirmationPage = () => {
  const { useNewConfirmation } = useConfirmationPageData()

  return (
    <ConfirmationMainPanel>
      {useNewConfirmation ? <NewConfirmation /> : <OldConfirmation />}
    </ConfirmationMainPanel>
  )
}

const ConfirmationPageComponent = () => {
  const history = useHistory()
  const location = useLocation()
  const { event, eventType, currentUser, isMeetingPollFlow } = useConfirmationPageData()
  const isTwoColumnDesign = useStatsigLayer('booking_flow_confirmation').get('confirmation_page_two_column_design')
  const returnPath = useMemo(() => {
    const pathMatch = location.pathname.match(/^(.*)\/invitees\//)
    return pathMatch ? pathMatch[1] : null
  }, [location.pathname])

  useEffect(() => {
    if (!returnPath) return undefined

    const unlisten = history.listen((_, action) => {
      if (action === 'POP') {
        window.location.href = returnPath
      }
    })

    return () => unlisten()
  }, [history, returnPath])
  // ───────────────────────────────────────────

  useEffect(() => { // On all confirmation pages, remove all potential past Stripe redirect stored info
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('pi_')) {
        localStorage.removeItem(key)
      }
    })
  }, [])

  if (eventType === undefined) return <Loader />
  if (event === undefined) return <Loader />
  if (event === null) {
    return (
      <ErrorAlert
        error={{}}
        buttonClick={() => window.location.reload()}
      />
    )
  }
  if (isMeetingPollFlow) {
    return (
      <ConfirmationMainPanel>
        <MeetingPollConfirmation />
      </ConfirmationMainPanel>
    )
  }
  if (currentUser) {
    return <CurrentConfirmationPage />
  }

  return isTwoColumnDesign ? <PersonalizedConfirmation /> : <CurrentConfirmationPage />
}

const PhoneVerificationFeature = () => {
  const { i18n } = useI18n()
  const phoneVerificationEnabled = useFlag('trust_and_safety_booking_sms_verification', false)
  const pendingPhoneVerification = useSelector(pendingPhoneVerificationSelector)
  const unverifiedPhoneNumber = useSelector(unverifiedPhoneNumberSelector)
  const inviteeUuid = useSelector(uuidSelector)
  const [isOpen, setIsOpen] = useState(true)
  const [verifyError, setVerifyError] = useState(null)

  const [verifySmsOtp, { isLoading: isVerifying }] = useVerifySmsOtpMutation()
  const [resendSmsOtp, { isLoading: isResending }] = useResendSmsOtpMutation()

  const handleVerify = useCallback(async (code) => {
    setVerifyError(null)
    const result = await verifySmsOtp({ inviteeUuid, verificationCode: code })
    if (result.error) {
      const errorCode = result.error?.payload?.errors?.verification || result.error?.payload?.errors?.e164_phone_number
      const i18nKey = SMS_OTP_ERROR_KEYS[errorCode]
      setVerifyError(i18nKey ? i18n.t(i18nKey) : i18n.t('booking.phone_verification.error.verification_generic'))
      throw new Error(errorCode)
    }
  }, [verifySmsOtp, inviteeUuid, i18n])

  const handleResend = useCallback(async () => {
    setVerifyError(null)
    await resendSmsOtp({ inviteeUuid })
  }, [resendSmsOtp, inviteeUuid])

  if (!phoneVerificationEnabled || !pendingPhoneVerification) return null

  return (
    <PhoneVerificationModal
      isOpen={isOpen}
      phoneNumber={unverifiedPhoneNumber}
      onVerify={handleVerify}
      onResend={handleResend}
      onComplete={() => setIsOpen(false)}
      isVerifying={isVerifying}
      isResending={isResending}
      error={verifyError}
    />
  )
}

export const ConfirmationPage = () => (
  <ConfirmationPageDataWrapper>
    <ConfirmationPageComponent />
    <PhoneVerificationFeature />
    <BookingStorage />
  </ConfirmationPageDataWrapper>
)
